/**
 * AuditSection 稽核 (S3.2) — record-derived audit facts only (created /
 * updated / >NT$200k decision with actor + reason / A1 acknowledgement /
 * checkpoint captures). No fabricated rows or timestamps. The global audit
 * lens (every ReasonRequiredModal write) arrives with governance in W10.
 */

import { Table, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { SectionHeader } from '@/components/products/ProductDetailPage/sections/SectionHeader';
import type { PurchaseOrder } from '@/types/api/purchaseOrder';
import type { VendorCheckpointRecord } from '@/stores/poStore';
import { useCheckpointLabel } from '../checkpoints';

interface AuditRow {
  key: string;
  label: string;
  detail: string | null;
  timestamp: string | null;
}

interface AuditSectionProps {
  po: PurchaseOrder;
  records: VendorCheckpointRecord[];
}

export const AuditSection = ({ po, records }: AuditSectionProps) => {
  const { t } = useTranslation(['procurement', 'common']);
  const { token } = theme.useToken();
  const cpLabel = useCheckpointLabel();

  const rows: AuditRow[] = [
    {
      key: 'created',
      label: t('procurement:detail.audit.created'),
      detail: null,
      timestamp: po.createdAt,
    },
    ...(po.updatedAt !== po.createdAt
      ? [
          {
            key: 'updated',
            label: t('procurement:detail.audit.updated'),
            detail: null,
            timestamp: po.updatedAt,
          },
        ]
      : []),
    ...(po.approval
      ? [
          {
            key: 'approval',
            label:
              po.approval.decision === 'approve'
                ? t('procurement:detail.audit.approvalApproved')
                : t('procurement:detail.audit.approvalRejected'),
            detail: `${t('procurement:detail.audit.decidedBy')}: ${po.approval.decidedBy} — ${
              po.approval.reason
            }`,
            timestamp: po.approval.decidedAt,
          },
        ]
      : []),
    ...(po.vendorAcknowledgedAt
      ? [
          {
            key: 'acknowledged',
            label: t('procurement:detail.audit.acknowledged'),
            detail: po.vendorConfirmationNote,
            timestamp: po.vendorAcknowledgedAt,
          },
        ]
      : []),
    ...records.map((record) => ({
      key: record.id,
      label: t('procurement:detail.audit.checkpointRecorded', {
        checkpoint: cpLabel(record.checkpoint),
      }),
      detail: record.note,
      timestamp: record.at,
    })),
  ].sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''));

  const columns: TableProps<AuditRow>['columns'] = [
    {
      title: t('procurement:detail.audit.event'),
      dataIndex: 'label',
      key: 'event',
      width: 260,
    },
    {
      title: t('procurement:detail.audit.detail'),
      dataIndex: 'detail',
      key: 'detail',
      render: (detail: string | null) => detail ?? renderDash(),
    },
    {
      title: t('procurement:detail.audit.timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 220,
      render: (timestamp: string | null) =>
        timestamp ? <TimestampDisplay date={timestamp} showLabel={false} /> : renderDash(),
    },
  ];

  return (
    <div>
      <SectionHeader title={t('procurement:detail.sections.audit')} />
      <TableContainer>
        <Table<AuditRow>
          rowKey="key"
          size="small"
          dataSource={rows}
          columns={columns}
          pagination={false}
        />
      </TableContainer>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', marginTop: token.marginSM, fontSize: token.fontSizeSM }}
      >
        {t('procurement:detail.audit.note')}
      </Typography.Text>
    </div>
  );
};

export default AuditSection;
